import { supabase } from '../supabaseClient'
import { extrairErroEdgeFunction, invokeEdgeFunction } from './edgeFunctionInvoke'

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL || 'https://ozttgvcetxxzdqfzeylt.supabase.co'

/** URL de callback para configurar no Meta Developer (WhatsApp → Configuração). */
export function getWhatsAppWebhookCallbackUrl() {
  return `${SUPABASE_URL.replace(/\/$/, '')}/functions/v1/whatsapp-webhook`
}

/**
 * Envia mensagem de teste via WhatsApp Cloud API (Meta).
 */
export async function enviarMensagemWhatsApp({
  to,
  type = 'text',
  message,
  template_id,
  template_name,
  template_language,
  template_components,
  body_preview,
  contato_nome,
  template_vars,
  origem,
  vendedor_id,
  usuario_id
}) {
  try {
    const body = { to, type }
    if (usuario_id) body.usuario_id = usuario_id
    if (type === 'template') {
      if (template_id) body.template_id = template_id
      if (template_name) body.template_name = template_name
      if (template_language) body.template_language = template_language
      if (Array.isArray(template_components) && template_components.length > 0) {
        body.template_components = template_components
      }
      if (body_preview) body.body_preview = body_preview
      if (contato_nome) body.contato_nome = contato_nome
      if (template_vars && typeof template_vars === 'object') body.template_vars = template_vars
      if (origem) body.origem = origem
      if (vendedor_id) body.vendedor_id = vendedor_id
    } else {
      body.message = message
    }

    const result = await invokeEdgeFunction('whatsapp-send-message', body)
    const data = result?.data
    const error = result?.error

    if (data && (data.error || data.success === false)) {
      const msg = data.error || 'Erro ao enviar mensagem.'
      console.error('[WhatsApp] Resposta de erro:', msg, data)
      return { success: false, error: msg, details: data.details }
    }

    if (error) {
      const msg = await extrairErroEdgeFunction(error, data)
      console.error('[WhatsApp] Erro da chamada:', error, 'data:', data)
      return { success: false, error: msg }
    }

    return {
      success: true,
      message_id: data?.message_id,
      to: data?.to,
      conversa_id: data?.conversa_id,
      message: data?.message || 'Mensagem enviada.',
      response: data?.response
    }
  } catch (e) {
    console.error('[WhatsApp] Exceção ao chamar Edge Function:', e)
    return {
      success: false,
      error: (e && e.message) || 'Falha ao chamar o serviço do WhatsApp.'
    }
  }
}

/**
 * Lista os templates de mensagem cadastrados na Meta (WhatsApp Cloud API).
 */
export async function listarTemplatesWhatsApp({ usuario_id, template_id } = {}) {
  try {
    const body = {}
    if (usuario_id) body.usuario_id = usuario_id
    if (template_id) body.template_id = template_id

    const result = await invokeEdgeFunction('whatsapp-list-templates', body)
    const data = result?.data
    const error = result?.error

    if (data && (data.error || data.success === false)) {
      const msg = data.error || 'Erro ao listar templates.'
      console.error('[WhatsApp] Erro ao listar templates:', msg, data)
      return {
        success: false,
        error: msg,
        templates: [],
        details: data.details,
        waba_id: data.waba_id_usado
      }
    }

    if (error) {
      const msg = await extrairErroEdgeFunction(error, data)
      console.error('[WhatsApp] Erro da chamada (templates):', error, 'data:', data)
      return { success: false, error: msg, templates: [] }
    }

    return {
      success: true,
      templates: Array.isArray(data?.templates) ? data.templates : [],
      waba_id: data?.waba_id,
      warning: data?.warning || null,
      count: data?.count
    }
  } catch (e) {
    console.error('[WhatsApp] Exceção ao listar templates:', e)
    return {
      success: false,
      error: (e && e.message) || 'Falha ao listar templates do WhatsApp.',
      templates: []
    }
  }
}

/** Carrega o token de verificação salvo na tabela whatsapp_config. */
export async function carregarWhatsAppConfig() {
  const { data, error } = await supabase
    .from('whatsapp_config')
    .select('verify_token, updated_at')
    .eq('id', 1)
    .maybeSingle()

  if (error) {
    console.error('[WhatsApp] Erro ao carregar config:', error)
    return { success: false, error: error.message }
  }

  return {
    success: true,
    verify_token: data?.verify_token || '',
    updated_at: data?.updated_at || null
  }
}

/** Salva o token de verificação (mesmo valor usado no Meta Developer). */
export async function salvarWhatsAppVerifyToken(verifyToken) {
  const token = String(verifyToken || '').trim()
  if (!token) {
    return { success: false, error: 'Informe o token de verificação.' }
  }

  const { error } = await supabase
    .from('whatsapp_config')
    .upsert({ id: 1, verify_token: token, updated_at: new Date().toISOString() })

  if (error) {
    console.error('[WhatsApp] Erro ao salvar config:', error)
    return { success: false, error: error.message }
  }

  return { success: true, message: 'Token de verificação salvo.' }
}

/** Lista eventos de webhook recebidos (mais recentes primeiro). */
export async function listarWebhookEventos({ limit = 50 } = {}) {
  const { data, error } = await supabase
    .from('whatsapp_webhook_eventos')
    .select('id, field, message_type, message_body, status, wa_id, contact_name, display_phone_number, message_id, received_at')
    .order('received_at', { ascending: false })
    .limit(limit)

  if (error) {
    console.error('[WhatsApp] Erro ao listar eventos:', error)
    return { success: false, error: error.message, eventos: [] }
  }

  return { success: true, eventos: data || [] }
}

/** Carrega o payload completo de um evento. */
export async function carregarWebhookPayload(eventoId) {
  const { data, error } = await supabase
    .from('whatsapp_webhook_eventos')
    .select('payload, received_at')
    .eq('id', eventoId)
    .maybeSingle()

  if (error) {
    return { success: false, error: error.message }
  }
  if (!data) {
    return { success: false, error: 'Evento não encontrado.' }
  }

  return { success: true, payload: data.payload, received_at: data.received_at }
}

/** Lista IDs de template salvos como parâmetro. */
export async function listarParametrosWhatsApp() {
  const { data, error } = await supabase
    .from('whatsapp_parametros')
    .select('id, template_id, nome, language, updated_at')
    .order('updated_at', { ascending: false })

  if (error) {
    console.error('[WhatsApp] Erro ao listar parâmetros:', error)
    return { success: false, error: error.message, parametros: [] }
  }

  return { success: true, parametros: data || [] }
}

/** Salva (ou atualiza) um ID de template como parâmetro. */
export async function salvarParametroWhatsApp({ template_id, nome, language } = {}) {
  const id = String(template_id || '').trim()
  if (!id) {
    return { success: false, error: 'Informe o ID do template.' }
  }

  const payload = {
    template_id: id,
    nome: nome ? String(nome).trim() : null,
    language: language ? String(language).trim() : null,
    updated_at: new Date().toISOString()
  }

  const { data, error } = await supabase
    .from('whatsapp_parametros')
    .upsert(payload, { onConflict: 'template_id' })
    .select('id, template_id, nome, language, updated_at')
    .single()

  if (error) {
    console.error('[WhatsApp] Erro ao salvar parâmetro:', error)
    return { success: false, error: error.message }
  }

  return { success: true, parametro: data, message: 'ID salvo como parâmetro.' }
}

/** Remove um ID salvo. */
export async function excluirParametroWhatsApp(id) {
  if (!id) {
    return { success: false, error: 'Informe o parâmetro a excluir.' }
  }

  const { error } = await supabase
    .from('whatsapp_parametros')
    .delete()
    .eq('id', id)

  if (error) {
    console.error('[WhatsApp] Erro ao excluir parâmetro:', error)
    return { success: false, error: error.message }
  }

  return { success: true, message: 'Parâmetro removido.' }
}

const TEMPLATES_INICIAR_OCULTOS_IDS = new Set([
  '1074704185236658',
  '1735858580774163'
])
const TEMPLATES_INICIAR_OCULTOS_NOMES = new Set([
  '3p_direct_integration_test_template',
  'hello_world'
])

export function templateOcultoIniciarConversa(tpl) {
  const id = String(tpl?.id || tpl?.template_id || '').trim()
  if (id && TEMPLATES_INICIAR_OCULTOS_IDS.has(id)) return true
  const nome = String(tpl?.name || tpl?.nome || '').trim().toLowerCase()
  return TEMPLATES_INICIAR_OCULTOS_NOMES.has(nome)
}

export function filtrarTemplatesIniciarConversa(lista) {
  return (lista || []).filter((tpl) => !templateOcultoIniciarConversa(tpl))
}

export function languageCodeTemplate(tpl) {
  if (!tpl) return ''
  if (typeof tpl.language === 'string') return tpl.language
  return tpl.language?.code || tpl.whatsapp_template_language || ''
}

/**
 * Lista templates da Meta a partir dos IDs salvos em whatsapp_parametros,
 * para atribuir a um usuário.
 */
export async function listarTemplatesDisponiveis({ usuario_id } = {}) {
  const paramsRes = await listarParametrosWhatsApp()
  const parametros = paramsRes.parametros || []
  const vistos = new Set()
  const templates = []

  for (const param of parametros) {
    const id = String(param.template_id || '').trim()
    if (!id) continue
    const res = await listarTemplatesWhatsApp({ usuario_id, template_id: id })
    const lista = filtrarTemplatesIniciarConversa(res.templates || [])
    for (const tpl of lista) {
      const key = tpl.id ? `id:${tpl.id}` : `${tpl.name || ''}::${languageCodeTemplate(tpl)}`
      if (vistos.has(key)) continue
      vistos.add(key)
      templates.push(tpl)
    }
  }

  templates.sort((a, b) => String(a.name || '').localeCompare(String(b.name || ''), 'pt-BR'))
  return { success: true, templates, error: paramsRes.error }
}

const NOME_TEMPLATE_HOMOLOGACAO = 'template_status_homologacao'
const NOME_TEMPLATE_PAGAR_FATURA = 'template_pagar_fatura'

export async function buscarTemplateWhatsAppPorNome({ usuario_id, nome } = {}) {
  const nomeAlvo = String(nome || '').trim().toLowerCase()
  if (!nomeAlvo) {
    return { success: false, error: 'Nome do template não informado.' }
  }

  const paramsRes = await listarParametrosWhatsApp()
  const parametros = paramsRes.parametros || []
  const param = parametros.find((p) => (
    String(p.nome || '').trim().toLowerCase() === nomeAlvo
  ))
  if (param?.template_id) {
    const res = await listarTemplatesWhatsApp({ usuario_id, template_id: param.template_id })
    const tpl = (res.templates || []).find((t) => (
      String(t.name || '').toLowerCase() === nomeAlvo
    )) || (res.templates || [])[0]
    if (tpl) {
      return {
        success: true,
        template: tpl,
        template_id: tpl.id || param.template_id,
        template_name: tpl.name || nomeAlvo,
        template_language: languageCodeTemplate(tpl) || param.language || 'pt_BR'
      }
    }
    return {
      success: true,
      template: null,
      template_id: param.template_id,
      template_name: param.nome || nomeAlvo,
      template_language: param.language || 'pt_BR'
    }
  }

  const disponiveis = await listarTemplatesDisponiveis({ usuario_id })
  const tpl = (disponiveis.templates || []).find((t) => (
    String(t.name || '').toLowerCase() === nomeAlvo
  ))
  if (!tpl) {
    return {
      success: false,
      error: `Template ${nomeAlvo} não encontrado nos parâmetros do WhatsApp.`
    }
  }
  return {
    success: true,
    template: tpl,
    template_id: tpl.id,
    template_name: tpl.name || nomeAlvo,
    template_language: languageCodeTemplate(tpl) || 'pt_BR'
  }
}

export async function buscarTemplateStatusHomologacao({ usuario_id } = {}) {
  return buscarTemplateWhatsAppPorNome({ usuario_id, nome: NOME_TEMPLATE_HOMOLOGACAO })
}

export async function buscarTemplatePagarFatura({ usuario_id } = {}) {
  return buscarTemplateWhatsAppPorNome({ usuario_id, nome: NOME_TEMPLATE_PAGAR_FATURA })
}
